/**
 * BackgroundGallery - Coupon background image selector component
 *
 * Story 4.3: Coupon Background Image Selection
 * Displays a grid of selectable background options for coupon creation.
 *
 * AC#1: Gallery UI - Grid of selectable background images
 * AC#2: No Background Option - First option is "No Background"
 * AC#3: Selection Visual - Selected image shows visual indicator
 */

import React from 'react';
import { useTranslation } from '@/i18n';
import { cn } from '@/lib/utils';
import { COUPON_BACKGROUNDS, type CouponBackground } from '@/config/couponBackgrounds';
import type { BackgroundGalleryProps } from '@/types/ui.types';

export function BackgroundGallery({ value, onChange, disabled = false }: BackgroundGalleryProps) {
	const { t } = useTranslation();

	const handleSelect = (background: CouponBackground) => {
		if (disabled) return;
		onChange(background.url);
	};

	return (
		<div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
			{COUPON_BACKGROUNDS.map((bg) => {
				const isSelected = value === bg.url;

				return (
					<button
						key={bg.id}
						type='button'
						onClick={() => handleSelect(bg)}
						disabled={disabled}
						className={cn(
							'relative aspect-video rounded-lg overflow-hidden transition-all',
							'border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
							isSelected
								? 'border-blue-500 ring-2 ring-blue-200 shadow-md'
								: 'border-gray-200 hover:border-gray-300',
							disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
						)}
						aria-label={t(bg.nameKey)}
						aria-pressed={isSelected}
					>
						{bg.url ? (
							// Background with image
							<img
								src={bg.url}
								alt={t(bg.nameKey)}
								className='w-full h-full object-cover'
								loading='lazy'
							/>
						) : (
							// "No Background" option
							<div className='w-full h-full bg-gray-100 flex items-center justify-center'>
								<span className='text-gray-400 text-xs sm:text-sm text-center px-2'>
									{t(bg.nameKey)}
								</span>
							</div>
						)}

						{/* Selection indicator (checkmark) */}
						{isSelected && (
							<div className='absolute top-1 right-1 bg-blue-500 rounded-full p-1'>
								<svg
									className='w-3 h-3 text-white'
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
								>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={3}
										d='M5 13l4 4L19 7'
									/>
								</svg>
							</div>
						)}

						{/* Label overlay for images */}
						{bg.url && (
							<div className='absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1'>
								<span className='text-white text-xs truncate block text-center'>
									{t(bg.nameKey)}
								</span>
							</div>
						)}
					</button>
				);
			})}
		</div>
	);
}
